import {
  Component,
  createResource,
  createSignal,
  createEffect,
  Show,
  For,
  createMemo,
} from "solid-js";
import { useNavigate } from "@solidjs/router";
import {
  api,
  listWorkItemLinks,
  createWorkItemLink,
  deleteWorkItemLink,
} from "../../api";
import type {
  WorkItem,
  WorkItemLinkSummary,
  WorkItemLinkRequestType,
  WorkItemListResponse,
  LinkedDocument,
  DocumentType,
} from "../../api";
import { useProjectPrefix } from "../../contexts/ProjectContext";

interface Props {
  workItemId: string;
  projectId: string;
  linkedDocuments?: LinkedDocument[];
}

const DOC_TYPE_CHIP = "bg-surface text-ink-2 border-line";

const DOC_TYPE_LABELS: Record<DocumentType, string> = {
  roadmap: "Roadmap",
  architecture: "Architecture",
  adr: "ADR",
  design: "Design",
  guide: "Guide",
  custom: "Custom",
};

const GROUP_ORDER: {
  label: string;
  pred: (l: WorkItemLinkSummary) => boolean;
}[] = [
  {
    label: "Blocks",
    pred: (l) => l.link_type === "blocks" && l.direction === "outgoing",
  },
  {
    label: "Blocked by",
    pred: (l) => l.link_type === "blocks" && l.direction === "incoming",
  },
  {
    label: "Duplicates",
    pred: (l) => l.link_type === "duplicates" && l.direction === "outgoing",
  },
  {
    label: "Duplicated by",
    pred: (l) => l.link_type === "duplicates" && l.direction === "incoming",
  },
  { label: "Relates to", pred: (l) => l.link_type === "relates_to" },
];

export const LinksSection: Component<Props> = (props) => {
  const prefix = useProjectPrefix();
  const navigate = useNavigate();

  const [version, setVersion] = createSignal(0);
  const [linkType, setLinkType] =
    createSignal<WorkItemLinkRequestType>("blocks");
  const [searchQuery, setSearchQuery] = createSignal("");
  const [selected, setSelected] = createSignal<WorkItem | null>(null);
  const [searchResults, setSearchResults] = createSignal<WorkItem[]>([]);
  const [searchOpen, setSearchOpen] = createSignal(false);

  const [data] = createResource(
    () => [props.workItemId, version()] as const,
    async ([id]) => listWorkItemLinks(id),
  );

  const groups = createMemo(() => {
    const links = data()?.links ?? [];
    return GROUP_ORDER.map((g) => ({
      label: g.label,
      items: links.filter(g.pred),
    })).filter((g) => g.items.length > 0);
  });

  const linkedIds = createMemo(
    () => new Set((data()?.links ?? []).map((l) => l.other.id)),
  );

  let searchTimeout: ReturnType<typeof setTimeout> | undefined;
  createEffect(() => {
    const q = searchQuery().trim();
    if (searchTimeout) clearTimeout(searchTimeout);
    if (!q) {
      setSearchResults([]);
      setSearchOpen(false);
      return;
    }
    searchTimeout = setTimeout(async () => {
      try {
        const res = await api.get<WorkItemListResponse>(
          `/projects/${props.projectId}/work-items?limit=200`,
        );
        const lower = q.toLowerCase();
        const blocked = linkedIds();
        const filtered = res.items
          .filter((item) => {
            if (item.id === props.workItemId) return false;
            if (blocked.has(item.id)) return false;
            const displayId = `${prefix()}-${item.item_number}`.toLowerCase();
            return (
              item.title.toLowerCase().includes(lower) ||
              displayId.includes(lower) ||
              String(item.item_number).includes(lower)
            );
          })
          .slice(0, 8);
        setSearchResults(filtered);
        setSearchOpen(true);
      } catch {
        setSearchResults([]);
        setSearchOpen(false);
      }
    }, 200);
  });

  const handleSelect = (item: WorkItem) => {
    setSelected(item);
    setSearchQuery(`${prefix()}-${item.item_number} ${item.title}`);
    setSearchOpen(false);
  };

  const handleRemove = async (linkId: string) => {
    await deleteWorkItemLink(props.workItemId, linkId);
    setVersion(version() + 1);
  };

  const handleSubmit = async (e: SubmitEvent) => {
    e.preventDefault();
    const target = selected();
    if (!target) return;
    await createWorkItemLink(props.workItemId, {
      target_work_item_id: target.id,
      link_type: linkType(),
    });
    setSearchQuery("");
    setSelected(null);
    setSearchResults([]);
    setSearchOpen(false);
    setVersion(version() + 1);
  };

  const docs = createMemo(() => props.linkedDocuments ?? []);
  const hasContent = createMemo(() => groups().length > 0 || docs().length > 0);

  return (
    <div class="bg-surface rounded border border-line p-4">
      <h3 class="text-sm font-medium text-ink-2 mb-3">Related</h3>
      <Show
        when={!data.loading}
        fallback={<div class="text-sm text-ink-3">Loading…</div>}
      >
        <Show
          when={hasContent()}
          fallback={<p class="text-sm text-ink-3">No related items yet.</p>}
        >
          <For each={groups()}>
            {(group) => (
              <div class="mb-3 last:mb-0">
                <h4 class="text-xs font-medium text-ink-3 mb-1">
                  {group.label}
                </h4>
                <ul class="divide-y divide-line">
                  <For each={group.items}>
                    {(link) => (
                      <li class="flex items-center justify-between py-1.5 gap-2">
                        <button
                          type="button"
                          class="flex-1 min-w-0 text-left flex items-baseline gap-2 hover:bg-sunken rounded px-1 -mx-1 py-0.5"
                          onClick={() => navigate(`/items/${link.other.id}`)}
                        >
                          <span class="text-xs text-ink-3 flex-shrink-0">
                            {prefix()}-{link.other.item_number}
                          </span>
                          <span class="text-sm text-ink truncate hover:underline">
                            {link.other.title}
                          </span>
                        </button>
                        <button
                          type="button"
                          class="text-xs text-ink-3 hover:text-signal-red flex-shrink-0"
                          onClick={() => handleRemove(link.id)}
                          aria-label="Remove link"
                        >
                          Remove
                        </button>
                      </li>
                    )}
                  </For>
                </ul>
              </div>
            )}
          </For>
          <Show when={docs().length > 0}>
            <div class="mb-3 last:mb-0">
              <h4 class="text-xs font-medium text-ink-3 mb-1">Documents</h4>
              <ul class="divide-y divide-line">
                <For each={docs()}>
                  {(doc) => (
                    <li class="py-1.5">
                      <button
                        type="button"
                        class="w-full text-left flex items-center gap-2 hover:bg-sunken rounded px-1 -mx-1 py-0.5"
                        onClick={() => navigate(`/documents/${doc.id}`)}
                      >
                        <span
                          class={
                            "inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full border flex-shrink-0 " +
                            DOC_TYPE_CHIP
                          }
                        >
                          {DOC_TYPE_LABELS[doc.doc_type]}
                        </span>
                        <span class="text-sm text-ink truncate hover:underline">
                          {doc.title}
                        </span>
                      </button>
                    </li>
                  )}
                </For>
              </ul>
            </div>
          </Show>
        </Show>
      </Show>
      <div class="mt-4 border-t border-line pt-3">
        <form class="flex gap-2 items-end" onSubmit={handleSubmit}>
          <label class="text-xs flex flex-col flex-1 relative">
            <span class="text-ink-3 mb-1">Search work item</span>
            <input
              type="text"
              class="border border-line rounded px-2 py-1 text-sm"
              value={searchQuery()}
              placeholder={`${prefix()}-42 or title`}
              aria-label="Search work item"
              onInput={(e) => {
                setSearchQuery(e.currentTarget.value);
                setSelected(null);
              }}
              onFocus={() => {
                if (searchResults().length > 0) setSearchOpen(true);
              }}
              autocomplete="off"
            />
            <Show when={searchOpen() && searchResults().length > 0}>
              <ul
                class="absolute top-full left-0 right-0 mt-1 bg-surface border border-line rounded shadow-overlay z-10 max-h-60 overflow-y-auto"
                role="listbox"
              >
                <For each={searchResults()}>
                  {(item) => (
                    <li>
                      <button
                        type="button"
                        class="w-full text-left px-2 py-1.5 hover:bg-sunken text-sm flex items-baseline gap-2"
                        onClick={() => handleSelect(item)}
                      >
                        <span class="text-xs text-ink-3 flex-shrink-0">
                          {prefix()}-{item.item_number}
                        </span>
                        <span class="text-ink truncate">{item.title}</span>
                      </button>
                    </li>
                  )}
                </For>
              </ul>
            </Show>
          </label>
          <label class="text-xs flex flex-col">
            <span class="text-ink-3 mb-1">Type</span>
            <select
              class="border border-line rounded px-2 py-1 text-sm"
              value={linkType()}
              onChange={(e) =>
                setLinkType(e.currentTarget.value as WorkItemLinkRequestType)
              }
            >
              <option value="blocks">Blocks</option>
              <option value="is_blocked_by">Is blocked by</option>
              <option value="relates_to">Relates to</option>
              <option value="duplicates">Duplicates</option>
            </select>
          </label>
          <button
            type="submit"
            disabled={!selected()}
            class="bg-ink text-paper text-sm rounded px-3 py-1 hover:bg-ink-2 focus-ring disabled:cursor-not-allowed"
          >
            Add link
          </button>
        </form>
      </div>
    </div>
  );
};

export default LinksSection;
